import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import {
  applyTargetBidUpdate,
  formatBidUpdateSuccessMessage,
  type EditableTargetBidTarget,
} from '@/api/targetBidUpdate';
import { cn } from '@/utils/cn';

type EditableTargetBidCellProps = {
  profileId: string;
  displayValue: string;
  editableTarget: EditableTargetBidTarget | null;
  targetLabel?: string;
  onUpdated?: () => void;
  className?: string;
};

export function EditableTargetBidCell({
  profileId,
  displayValue,
  editableTarget,
  targetLabel,
  onUpdated,
  className,
}: EditableTargetBidCellProps) {
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [draftBid, setDraftBid] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const mutation = useMutation({
    mutationFn: async (newBid: number) => {
      if (!editableTarget) throw new Error('Bid is not editable');
      await applyTargetBidUpdate(profileId, editableTarget, newBid);
      return newBid;
    },
    onSuccess: (newBid) => {
      showToast(formatBidUpdateSuccessMessage(newBid), 'success');
      setIsOpen(false);
      onUpdated?.();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to update bid';
      showToast(
        `Failed to update bid${targetLabel ? ` for ${targetLabel}` : ''}: ${message}`,
        'error'
      );
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const openEditor = () => {
    if (!editableTarget) return;
    setDraftBid(String(editableTarget.bid));
    setIsOpen(true);
  };

  const handleApply = () => {
    const parsed = Number(draftBid);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showToast('Enter a valid bid greater than 0', 'error');
      return;
    }
    mutation.mutate(parsed);
  };

  if (!editableTarget) {
    return <span className={className}>{displayValue}</span>;
  }

  return (
    <div ref={containerRef} className={cn('relative inline-block text-right', className)}>
      <button
        type="button"
        onClick={openEditor}
        className="inline-flex h-7 min-w-[4.75rem] cursor-pointer items-center justify-center rounded-lg border border-border bg-surface px-2 font-mono text-body-sm tabular-nums text-foreground transition-colors hover:border-brand hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
        aria-label={targetLabel ? `Edit bid for ${targetLabel}` : 'Edit bid'}
      >
        {displayValue}
      </button>

      {isOpen ? (
        <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded-xl border border-border bg-surface p-3 shadow-lg">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={draftBid}
            onChange={(event) => setDraftBid(event.target.value)}
            className="mb-2 h-7 px-2 py-1 text-right font-mono text-sm"
            autoFocus
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleApply();
              }
            }}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setIsOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleApply} disabled={mutation.isPending}>
              {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
